import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface ContextPanelState {
  isOpen: boolean;
  content: ReactNode | null;
  open: (content: ReactNode) => void;
  close: () => void;
}

const ContextPanelContext = createContext<ContextPanelState>({
  isOpen: false,
  content: null,
  open: () => {},
  close: () => {},
});

export function useContextPanel() {
  return useContext(ContextPanelContext);
}

export function ContextPanelProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<ReactNode | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback((node: ReactNode) => {
    setContent(node);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setContent(null);
  }, []);

  return (
    <ContextPanelContext.Provider value={{ isOpen, content, open, close }}>
      {children}
    </ContextPanelContext.Provider>
  );
}

export function ContextPanel() {
  const { isOpen, content, close } = useContextPanel();

  if (!isOpen || !content) return null;

  return (
    <>
      {/* Backdrop for click-outside on lg */}
      <div
        className="hidden lg:block fixed inset-0 z-30"
        onClick={close}
        aria-hidden
      />
      <aside
        className={cn(
          "hidden lg:flex flex-col w-[300px] shrink-0 border-l border-border bg-background z-40 relative",
          "animate-in slide-in-from-right-5 duration-200"
        )}
      >
        <div className="flex items-center justify-end p-2">
          <button
            onClick={close}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center"
            aria-label="Fermer le panneau"
            title="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-3 pb-4">
          {content}
        </div>
      </aside>
    </>
  );
}
